import React from 'react'
import { Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  ChartBarIcon,
  ArrowRightIcon,
  CogIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

const ruleBuilderTypes = [
  {
    id: 'combined',
    name: 'Unified Rules Builder',
    description: 'Build rules across transactions and gaming in one place',
    icon: CogIcon,
    href: '/monitor-safe/builder/combined',
    color: 'blue',
    features: [
      'Transaction and Session support',
      'Cumulative/session-based processing',
      'Field-operator conditions',
      'SMR/Regulatory tagging',
      'Inline test runner'
    ],
    examples: [
      'Cash-ins over $10,000 within 24h window',
      'Gaming session > 4 hours with high turnover',
      'Structuring across days (SMR)'
    ]
  }
]

function RuleBuilderMain() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Spacer to align with persistent header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2" />

      {/* Rule Builder Types */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 gap-8">
          {ruleBuilderTypes.map((type) => (
            <div key={type.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 bg-${type.color}-100 rounded-lg flex items-center justify-center`}>
                      <type.icon className={`w-6 h-6 text-${type.color}-600`} />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-semibold text-gray-900">{type.name}</h3>
                      <p className="text-gray-600">{type.description}</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Features:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {type.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <div className={`w-2 h-2 bg-${type.color}-400 rounded-full mr-2`}></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Examples */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Example Rules:</h4>
                  <div className="space-y-2">
                    {type.examples.map((example, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <ExclamationTriangleIcon className={`w-4 h-4 text-${type.color}-500 mr-2`} />
                        {example}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  to={type.href}
                  className={`w-full flex items-center justify-center px-4 py-3 bg-${type.color}-600 hover:bg-${type.color}-700 text-white font-medium rounded-lg transition-colors duration-200`}
                >
                  Build {type.name}
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">50+</div>
              <div className="text-sm text-gray-600">Available Fields</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">12</div>
              <div className="text-sm text-gray-600">Rule Types</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">24/7</div>
              <div className="text-sm text-gray-600">Real-time Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">5ms</div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RuleBuilderMain